import React, { useState } from 'react';
import { useAuth } from '../AuthContext';
import {
  Container,
  Paper,
  Typography,
  Box,
  Link,
  Divider,
  Fade,
  TextField,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import { styled } from '@mui/system';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';

// Modern color palette
const colors = {
  primary: '#2ecc71',
  secondary: '#34495e',
  accent: '#3498db',
  background: '#f5f7fa',
  gradientStart: 'rgba(62,180,137,1)',
  gradientEnd: 'rgba(144,238,144,1)',
};

// Styled components
const ContactPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(5),
  borderRadius: '20px',
  background: '#ffffff',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.05)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  overflow: 'hidden',
  position: 'relative',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 15px 50px rgba(0, 0, 0, 0.1)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  background: `linear-gradient(90deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
  padding: theme.spacing(3),
  borderRadius: '20px 20px 0 0',
  color: '#fff',
  textAlign: 'center',
  margin: '-24px -24px 24px -24px',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    margin: '-16px -16px 16px -16px',
  },
}));

const InfoBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  borderRadius: '12px',
  backgroundColor: '#fafafa',
  transition: 'background-color 0.3s ease',
  '&:hover': {
    backgroundColor: '#f0f4f8',
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
  },
}));

const StyledLink = styled(Link)(({ theme }) => ({
  color: colors.accent,
  fontWeight: 500,
  textDecoration: 'none',
  transition: 'color 0.3s ease',
  wordBreak: 'break-all',
  overflowWrap: 'break-word',
  '&:hover': {
    color: colors.primary,
    textDecoration: 'underline',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: '600',
  padding: theme.spacing(1.5, 3),
  transition: 'all 0.3s ease, transform 0.2s ease',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  touchAction: 'manipulation',
  '&:hover': {
    backgroundColor: colors.accent,
    color: 'white',
    transform: 'scale(1.05)',
    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.15)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 2),
    fontSize: '0.9rem',
  },
}));

const ContactUs = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: user?.displayName || '',
    email: user?.email || '',
    message: '',
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      setSnackbarMessage('Please fill in all fields.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    console.log('Sending email to ginoleon98@gmail.com with the following data:', {
      to: 'mathsgemseychelles@gmail.com',
      from: formData.email,
      subject: `New Message from ${formData.name} via Maths Gem Seychelles`,
      body: formData.message,
    });

    setSnackbarMessage('Your message has been sent successfully!');
    setSnackbarSeverity('success');
    setOpenSnackbar(true);

    setFormData({
      name: user?.displayName || '',
      email: user?.email || '',
      message: '',
    });
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  return (
    <Container maxWidth="sm" sx={{ marginTop: { xs: 8, sm: 10 } }}>
      <br/>
      <Fade in timeout={800}>
        <ContactPaper elevation={8}>
          <HeaderBox>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1,
                fontSize: { xs: '1.8rem', sm: '2.125rem' },
              }}
            >
              Contact Us
            </Typography>
            <Typography
              variant="body1"
              sx={{ opacity: 0.9, fontSize: { xs: '0.9rem', sm: '1rem' } }}
            >
              Connect with Maths Gem Seychelles
            </Typography>
          </HeaderBox>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Email */}
            <InfoBox>
              <EmailIcon sx={{ color: colors.primary, fontSize: { xs: 24, sm: 30 } }} />
              <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: colors.secondary,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  }}
                >
                  Email
                </Typography>
                <StyledLink href="mailto:mathsgemseychelles@gmail.com" variant="body1">
                  mathsgemseychelles@gmail.com
                </StyledLink>
              </Box>
            </InfoBox>

            {/* Address */}
            <InfoBox>
              <LocationOnIcon sx={{ color: colors.primary, fontSize: { xs: 24, sm: 30 } }} />
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: colors.secondary,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  }}
                >
                  Address
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: colors.secondary,
                    fontSize: { xs: '0.85rem', sm: '1rem' },
                  }}
                >
                  Anse a la Mouche, Baie Lazare, Mahe, Seychelles
                </Typography>
              </Box>
            </InfoBox>

            {/* Contact */}
            <InfoBox>
              <PhoneIcon sx={{ color: colors.primary, fontSize: { xs: 24, sm: 30 } }} />
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: colors.secondary,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  }}
                >
                  Contact
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: colors.secondary,
                    fontSize: { xs: '0.85rem', sm: '1rem' },
                  }}
                >
                  +248 282 5054
                </Typography>
              </Box>
            </InfoBox>
          </Box>

          {user && (
            <>
              <Divider sx={{ my: 3, borderColor: 'rgba(0, 0, 0, 0.1)' }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: colors.secondary,
                  mb: 2,
                  fontSize: { xs: '1.2rem', sm: '1.5rem' },
                }}
              >
                Have a Question or Topic Suggestion?
              </Typography>
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                <TextField
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  fullWidth
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      '& fieldset': { borderColor: colors.primary },
                      '&:hover fieldset': { borderColor: colors.accent },
                      '&.Mui-focused fieldset': { borderColor: colors.accent },
                    },
                    '& .MuiInputLabel-root': {
                      color: colors.secondary,
                      '&.Mui-focused': { color: colors.accent },
                    },
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  }}
                  disabled
                />
                <TextField
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  fullWidth
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      '& fieldset': { borderColor: colors.primary },
                      '&:hover fieldset': { borderColor: colors.accent },
                      '&.Mui-focused fieldset': { borderColor: colors.accent },
                    },
                    '& .MuiInputLabel-root': {
                      color: colors.secondary,
                      '&.Mui-focused': { color: colors.accent },
                    },
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  }}
                  disabled
                />
                <TextField
                  label="Your Message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={4}
                  placeholder="E.g., I have a question about algebra, or I'd like to suggest a topic on geometry."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      '& fieldset': { borderColor: colors.primary },
                      '&:hover fieldset': { borderColor: colors.accent },
                      '&.Mui-focused fieldset': { borderColor: colors.accent },
                    },
                    '& .MuiInputLabel-root': {
                      color: colors.secondary,
                      '&.Mui-focused': { color: colors.accent },
                    },
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  }}
                />
                <StyledButton
                  type="submit"
                  variant="contained"
                  sx={{
                    background: `linear-gradient(45deg, ${colors.primary}, ${colors.accent})`,
                    color: 'white',
                    mt: 1,
                  }}
                >
                  Send Message
                </StyledButton>
              </Box>
            </>
          )}

          <Divider sx={{ my: 3, borderColor: 'rgba(0, 0, 0, 0.1)' }} />

          <Typography
            variant="body2"
            align="center"
            sx={{
              color: colors.secondary,
              opacity: 0.8,
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
            }}
          >
            We’re here to assist you with your learning journey. Reach out anytime!
          </Typography>
        </ContactPaper>
      </Fade>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ContactUs;